interface WithdrawDialogProps {
  isOpen: boolean;
  loading: boolean;
  reason: string;
  onReasonChange: (reason: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

export function WithdrawDialog({
  isOpen,
  loading,
  reason,
  onReasonChange,
  onConfirm,
  onCancel,
}: Readonly<WithdrawDialogProps>) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-bold text-white mb-4">Withdraw Submission?</h3>
        <p className="text-neutral-400 mb-4">
          This will return your submission to draft status and remove it from the review queue. You'll be able to make
          changes and resubmit.
        </p>
        <div className="mb-4">
          <label htmlFor="withdraw-reason" className="text-sm text-neutral-400 block mb-2">
            Reason (optional)
          </label>
          <textarea
            id="withdraw-reason"
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="Why are you withdrawing this submission?"
            rows={3}
            className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-lumi-accent resize-none"
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-neutral-800 text-white rounded-lg hover:bg-neutral-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Withdrawing..." : "Withdraw"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface StatusBannerProps {
  status: string;
}

export function StatusBanner({ status }: Readonly<StatusBannerProps>) {
  if (status === "PENDING") {
    return (
      <div className="bg-yellow-600/10 border border-yellow-600/30 rounded-lg p-4 mb-6">
        <p className="text-yellow-400 text-sm">
          <strong>This submission is under review.</strong> It cannot be edited while pending. You can withdraw it to
          make changes.
        </p>
      </div>
    );
  }

  if (status === "APPROVED") {
    return (
      <div className="bg-green-600/10 border border-green-600/30 rounded-lg p-4 mb-6">
        <p className="text-green-400 text-sm">
          <strong>This submission has been approved.</strong> It is read-only and will be published soon.
        </p>
      </div>
    );
  }

  if (status === "PUBLISHED") {
    return (
      <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4 mb-6">
        <p className="text-blue-400 text-sm">
          <strong>This submission is published.</strong> It is now live on the platform.
        </p>
      </div>
    );
  }

  return null;
}

// Re-export SubmissionContent from ViewSubmissionSections
export { SubmissionContent } from "./ViewSubmissionSections";
